


import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardShell from './components/DashboardShell';

function PageShell({ title, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-gray-900">Firm Dashboard</span>
            <span className="text-gray-300">/</span>
            <span className="text-gray-700">{title}</span>
          </div>
        </div>
      </div>
      {/* Content area */}
      <main>
        {children}
      </main>
    </div>
  );
}

/**
 * LeadsPage.jsx
 * Tailwind-styled Leads manager for pre-case funnel.
 *
 * Assumptions:
 * - JWT access token is stored in localStorage under one of: 'access', 'jwt', 'token'.
 * - API base URL is in REACT_APP_API_BASE (e.g. https://api.cisowskilaw.com) with 
 *   routes mounted at /api/. Example: `${API}/leads/`.
 */

const RAW_BASE = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || '').trim();
const BASE_NO_SLASH = RAW_BASE.replace(/\/+$/, '');
// If no env provided, try same-origin '/api' (useful for local dev with a proxy)
const API = BASE_NO_SLASH ? (BASE_NO_SLASH.endsWith('/api') ? BASE_NO_SLASH : `${BASE_NO_SLASH}/api`) : '/api';

async function request(url, { method = 'GET', params, body, headers } = {}) {
  const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
  const opts = { method, headers: { ...(headers || {}), ...authHeaders() } };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${url}${qs}`, opts);
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch (_) { data = text; }
  if (!res.ok) {
    const detail = (data && (data.detail || data.non_field_errors)) || res.statusText || 'Request failed';
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'consult_scheduled', label: 'Consult Scheduled' },
  { value: 'consult_no_show', label: 'Consult No-Show' },
  { value: 'follow_up', label: 'Follow-Up Needed' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'retained', label: 'Retained' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'lost', label: 'Lost' },
  { value: 'invalid', label: 'Invalid/Spam' },
];

const SOURCE_OPTIONS = [
  { value: 'quick_intake', label: 'Quick Intake' },
  { value: 'google', label: 'Google' },
  { value: 'mail', label: 'Mail' },
  { value: 'consultation', label: 'Consultation Booking' },
  { value: 'call', label: 'Phone Call' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

function Badge({ children, tone = 'indigo' }) {
  const color = {
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    green: 'bg-green-50 text-green-700 ring-green-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    slate: 'bg-slate-50 text-slate-700 ring-slate-200',
    sky: 'bg-sky-50 text-sky-700 ring-sky-200',
    violet: 'bg-violet-50 text-violet-700 ring-violet-200',
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${color}`}>
      {children}
    </span>
  );
}

function toneForStatus(status) {
  switch (status) {
    case 'new': return 'indigo';
    case 'contacted': return 'sky';
    case 'consult_scheduled': return 'violet';
    case 'consult_no_show': return 'amber';
    case 'follow_up': return 'amber';
    case 'evaluation': return 'indigo';
    case 'retained': return 'green';
    case 'not_interested': return 'slate';
    case 'lost': return 'red';
    case 'invalid': return 'red';
    default: return 'indigo';
  }
}

function formatDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString();
}

function EmptyState({ onAdd }) {
  return (
    <div className="text-center py-20">
      <div className="mx-auto h-14 w-14 rounded-full bg-indigo-50 ring-1 ring-inset ring-indigo-100 flex items-center justify-center">
        <svg className="h-7 w-7 text-indigo-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </div>
      <h3 className="mt-6 text-lg font-semibold text-gray-900">No leads yet</h3>
      <p className="mt-2 text-sm text-gray-600">Get started by creating your first lead.</p>
      <div className="mt-6">
        <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-white shadow hover:bg-indigo-500">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
          Add lead
        </button>
      </div>
    </div>
  );
}

function LeadFormModal({ open, onClose, onSubmit, initial }) {
  const [form, setForm] = useState(() => initial || { status: 'new', source: 'other', priority: 3 });
  useEffect(() => setForm(initial || { status: 'new', source: 'other', priority: 3 }), [initial]);

  const notesRef = useRef(null);
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    if (notesRef.current) autoGrow(notesRef.current);
  }, [form.notes]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white ring-1 ring-gray-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{initial ? 'Edit Lead' : 'Add Lead'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Name</label>
              <input value={form.name || ''} onChange={e=>update('name', e.target.value)} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" required />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Phone</label>
              <input value={form.phone || ''} onChange={e=>update('phone', e.target.value)} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email || ''} onChange={e=>update('email', e.target.value)} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Priority</label>
              <input type="number" min={1} max={5} value={form.priority ?? 3} onChange={e=>update('priority', Number(e.target.value))} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Status</label>
              <select value={form.status || 'new'} onChange={e=>update('status', e.target.value)} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Source</label>
              <select value={form.source || 'other'} onChange={e=>update('source', e.target.value)} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Next follow-up</label>
              <input type="datetime-local" value={form.next_followup_at ? form.next_followup_at.substring(0,16) : ''} onChange={e=>update('next_followup_at', e.target.value)} className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Notes</label>
            <textarea
              ref={notesRef}
              rows={4}
              value={form.notes || ''}
              onChange={e=>update('notes', e.target.value)}
              onInput={(e)=>autoGrow(e.target)}
              placeholder="Add any context, call notes, promises to pay, etc."
              className="w-full rounded-md bg-white border border-gray-300 p-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-40 max-h-[70vh] overflow-auto resize-none"
            ></textarea>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-500">{initial ? 'Save changes' : 'Create lead'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function daysFromNowISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export default function LeadsPage() {
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [ordering, setOrdering] = useState('-created_at');
  const [excludedStatuses, setExcludedStatuses] = useState(['lost', 'invalid', 'not_interested']); // default exclusions
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [excludedSources, setExcludedSources] = useState([]); // sources to hide
  const [excludeSourcesOpen, setExcludeSourcesOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Removed followMenuFor state; popover replaced with prompt
  const [scheduling, setScheduling] = useState(false);
  const [followupLead, setFollowupLead] = useState(null);
  const [customDays, setCustomDays] = useState('');


  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (q) params.q = q;
      // if (status) params.status = status; // REMOVED: filter by single status is no longer used
      if (source) params.source = source;
      if (ordering) params.ordering = ordering;
      const data = await request(`${API}/leads/`, { params });
      const items = Array.isArray(data) ? data : (data && data.results) ? data.results : [];
      setLeads(items);
    } catch (e) {
      setError(e?.message || e?.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, [ordering]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchLeads();
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (lead) => { setEditing(lead); setModalOpen(true); };

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        await request(`${API}/leads/${editing.id}/`, { method: 'PATCH', body: payload });
      } else {
        await request(`${API}/leads/`, { method: 'POST', body: payload });
      }
      setModalOpen(false);
      setEditing(null);
      fetchLeads();
    } catch (e) {
      alert(e?.message || (e?.response?.data ? JSON.stringify(e.response.data) : 'Save failed'));
    }
  };

  const handleDelete = async (lead) => {
    try {
      await request(`${API}/leads/${lead.id}/`, { method: 'DELETE' });
      setConfirmDelete(null);
      fetchLeads();
    } catch (e) {
      alert(e?.message || 'Delete failed');
    }
  };

  const scheduleFollowup = async (lead, days) => {
    try {
      setScheduling(true);
      let whenISO;
      if (days === 'custom') {
        const input = window.prompt('Follow up in how many days?', '7');
        if (!input) { setScheduling(false); return; }
        const n = Number(input);
        if (!Number.isFinite(n) || n < 0) { alert('Please enter a valid number of days.'); setScheduling(false); return; }
        whenISO = daysFromNowISO(n);
      } else {
        whenISO = daysFromNowISO(days);
      }
      await request(`${API}/leads/${lead.id}/schedule-followup/`, { method: 'POST', body: { next_followup_at: whenISO } });
      setFollowupLead(null);
      fetchLeads();
    } catch (e) {
      alert(e?.message || 'Failed to schedule follow-up');
    } finally {
      setScheduling(false);
    }
  };

  const visibleLeads = useMemo(
    () => leads.filter(l => !excludedStatuses.includes(l.status) && !excludedSources.includes(l.source)),
    [leads, excludedStatuses, excludedSources]
  );

  const table = useMemo(() => (
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
      <div className="">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-700">Lead</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">Case</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">Source</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">Priority</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">Next Follow-up</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">Updated</th>
              <th className="py-3.5 pl-3 pr-6 text-right text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {visibleLeads.map((l) => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="py-3 pl-6 pr-3 break-words">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{l.name}</span>
                    <span className="text-xs text-gray-600">{l.phone || '—'} • {l.email || '—'}</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-sm">
                  {(() => {
                    const caseId = l.converted_case_case_id || (l.converted_case && l.converted_case.case_id) || l.case_id;
                    const href = l.converted_case_url || (caseId ? `https://app.cisowskilaw.com/case/${caseId}` : null);
                    if (href && caseId) {
                      return (
                        <a
                          href={href}
                          className="text-indigo-600 hover:text-indigo-800 hover:underline break-words"
                          title={href}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {caseId}
                        </a>
                      );
                    }
                    if (l.converted_case) {
                      return <Badge tone="slate">Linked</Badge>;
                    }
                    return <span className="text-gray-400">—</span>;
                  })()}
                </td>
                <td className="px-3 py-3 text-sm">
                  <Badge tone="slate">{l.source}</Badge>
                </td>
                <td className="px-3 py-3 text-sm">
                  <Badge tone={toneForStatus(l.status)}>{l.status}</Badge>
                </td>
                <td className="px-3 py-3 text-sm text-gray-700">{l.priority ?? '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-700">{formatDateTime(l.next_followup_at)}</td>
                <td className="px-3 py-3 text-sm text-gray-700">{formatDateTime(l.updated_at)}</td>
                <td className="py-3 pl-3 pr-6 text-right">
                  <div className="relative inline-flex items-center gap-2">
                    <button onClick={() => openEdit(l)} className="rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
                    <button onClick={() => setConfirmDelete(l)} className="rounded-md border border-red-300 text-red-700 hover:bg-red-50 px-2.5 py-1 text-sm">Delete</button>

                    <button
                      type="button"
                      onClick={() => { setFollowupLead(l); setCustomDays(''); }}
                      className="rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Schedule follow‑up
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visibleLeads.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10">
                  <EmptyState onAdd={openCreate} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  ), [visibleLeads, scheduleFollowup]);
// Checkbox row helper for exclude status dropdown
function Checkbox({ checked, onChange }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
      checked={!!checked}
      onChange={e => onChange(e.target.checked)}
    />
  );
}

  return (
    <DashboardShell title="Leads">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* existing header, filters, table, modals remain unchanged below */}
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
            <p className="mt-1 text-sm text-gray-600">Track all pre-case contacts and never let a lead slip through.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-white shadow hover:bg-indigo-500">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
              New Lead
            </button>
            <button onClick={fetchLeads} className="rounded-md border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">Refresh</button>
          </div>
        </header>

        {/* Filters */}
        <form onSubmit={onSearch} className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Name, email, phone, notes…" className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 pl-9 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 18a8 8 0 110-16 8 8 0 010 16z"/></svg>
            </div>
          </div>
          <div className="relative">
            <label className="block text-sm text-gray-700 mb-1">Exclude statuses</label>
            <button
              type="button"
              onClick={() => setExcludeOpen(v => !v)}
              className="w-full inline-flex items-center justify-between rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {excludedStatuses.length === 0 ? 'None' : `${excludedStatuses.length} selected`}
              <svg className="h-4 w-4 text-gray-400 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6"/></svg>
            </button>
            {excludeOpen && (
              <div className="absolute z-10 mt-1 w-64 rounded-md bg-white shadow-lg ring-1 ring-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Select statuses to hide</span>
                  <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setExcludedStatuses([])}>Clear</button>
                </div>
                <div className="max-h-64 overflow-auto space-y-2 pr-1">
                  {STATUS_OPTIONS.map(o => (
                    <label key={o.value} className="flex items-center gap-2 text-sm text-gray-800">
                      <Checkbox
                        checked={excludedStatuses.includes(o.value)}
                        onChange={(checked) => {
                          setExcludedStatuses(prev => checked ? [...prev, o.value] : prev.filter(v => v !== o.value));
                        }}
                      />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-end">
                  <button type="button" className="rounded-md bg-indigo-600 px-3 py-1.5 text-white text-sm hover:bg-indigo-500" onClick={() => setExcludeOpen(false)}>Done</button>
                </div>
              </div>
            )}
            {/* show selected as chips */}
            {excludedStatuses.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {excludedStatuses.map(v => {
                  const label = (STATUS_OPTIONS.find(s => s.value === v) || {}).label || v;
                  return (
                    <span key={v} className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 ring-1 ring-gray-200 px-2 py-0.5 text-xs">
                      {label}
                      <button type="button" className="ml-1 text-gray-500 hover:text-gray-700" onClick={() => setExcludedStatuses(prev => prev.filter(x => x !== v))}>
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Source</label>
            <select value={source} onChange={e=>setSource(e.target.value)} className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">All</option>
              {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="relative">
            <label className="block text-sm text-gray-700 mb-1">Exclude sources</label>
            <button
              type="button"
              onClick={() => setExcludeSourcesOpen(v => !v)}
              className="w-full inline-flex items-center justify-between rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {excludedSources.length === 0 ? 'None' : `${excludedSources.length} selected`}
              <svg className="h-4 w-4 text-gray-400 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6"/></svg>
            </button>
            {excludeSourcesOpen && (
              <div className="absolute z-10 mt-1 w-64 rounded-md bg-white shadow-lg ring-1 ring-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Select sources to hide</span>
                  <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setExcludedSources([])}>Clear</button>
                </div>
                <div className="max-h-64 overflow-auto space-y-2 pr-1">
                  {SOURCE_OPTIONS.map(o => (
                    <label key={o.value} className="flex items-center gap-2 text-sm text-gray-800">
                      <Checkbox
                        checked={excludedSources.includes(o.value)}
                        onChange={(checked) => {
                          setExcludedSources(prev => checked ? [...prev, o.value] : prev.filter(v => v !== o.value));
                        }}
                      />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-end">
                  <button type="button" className="rounded-md bg-indigo-600 px-3 py-1.5 text-white text-sm hover:bg-indigo-500" onClick={() => setExcludeSourcesOpen(false)}>Done</button>
                </div>
              </div>
            )}
            {/* show selected as chips */}
            {excludedSources.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {excludedSources.map(v => {
                  const label = (SOURCE_OPTIONS.find(s => s.value === v) || {}).label || v;
                  return (
                    <span key={v} className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 ring-1 ring-gray-200 px-2 py-0.5 text-xs">
                      {label}
                      <button type="button" className="ml-1 text-gray-500 hover:text-gray-700" onClick={() => setExcludedSources(prev => prev.filter(x => x !== v))}>
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Order by</label>
            <select value={ordering} onChange={e=>setOrdering(e.target.value)} className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="-created_at">Newest</option>
              <option value="created_at">Oldest</option>
              <option value="next_followup_at">Next follow-up asc</option>
              <option value="-next_followup_at">Next follow-up desc</option>
              <option value="priority">Priority asc</option>
              <option value="-priority">Priority desc</option>
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" className="w-full rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-500">Apply</button>
          </div>
        </form>

        {/* Table */}
        {error && (
          <div className="mb-4 rounded-md bg-red-50 ring-1 ring-inset ring-red-200 p-3 text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl bg-white ring-1 ring-gray-200 p-10 text-center text-gray-600">Loading leads…</div>
        ) : (
          table
        )}

        {/* Modals */}
        <LeadFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSubmit={handleSubmit} initial={editing} />

        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white ring-1 ring-gray-200 shadow-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900">Delete this lead?</h3>
              <p className="mt-2 text-sm text-gray-600">This action cannot be undone.</p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button onClick={() => setConfirmDelete(null)} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={() => handleDelete(confirmDelete)} className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-500">Delete</button>
              </div>
            </div>
          </div>
        )}
        {followupLead && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white ring-1 ring-gray-200 shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Schedule follow-up</h3>
                <button
                  onClick={() => setFollowupLead(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
              <div className="px-6 py-5">
                <p className="text-sm text-gray-700 mb-4">Choose a quick option or enter custom days from today.</p>
                <div className="grid grid-cols-4 gap-2">
                  <button onClick={() => scheduleFollowup(followupLead, 7)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">7 days</button>
                  <button onClick={() => scheduleFollowup(followupLead, 14)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">14 days</button>
                  <button onClick={() => scheduleFollowup(followupLead, 30)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">30 days</button>
                  <button onClick={() => scheduleFollowup(followupLead, 60)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">60 days</button>
                </div>
                <div className="mt-5">
                  <label className="block text-sm text-gray-700 mb-1">Custom days</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      placeholder="e.g. 10"
                      className="w-32 rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={async () => {
                        const n = Number(customDays);
                        if (!Number.isFinite(n) || n < 0) { alert('Enter a valid number'); return; }
                        await scheduleFollowup(followupLead, n);
                        setFollowupLead(null);
                      }}
                      className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-500"
                    >
                      Schedule
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button onClick={() => setFollowupLead(null)} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}